import React from "react";

import { Grid } from "@material-ui/core";

import {
  CheckboxGroupInput,
  Edit,
  FormDataConsumer,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
} from "react-admin";

import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { getUserTypeAcls } from "../../../utils/appUtils";
import { REGEX_PHONE, UserType } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";
import { CustomerEditHelp } from "./CustomerHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";
import { ValidateSaveButton } from "./ValidateSaveButton";
import { CustomerAside } from "./CustomerAside";
import { NewBadge } from "../../../components/common/FeatureBadges";
import { SendInviteButton } from "../../../components/common/SendInviteButton";

const validateRequired = required();
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const CustomerEditForm = ({ record, ...props }) => {
  const aclList = getUserTypeAcls(UserType.CUSTOMER);

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Grid container direction="column" spacing={1}>
              {window.appUser.type !== UserType.CUSTOMER && (
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} lg={6}>
                    <TextInput
                      source="reloadYardAccounts.items[0].accountName"
                      label="Account"
                      variant="outlined"
                      fullWidth
                      validate={validateRequired}
                      disabled={true}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="firstName"
                    label="First Name"
                    variant="outlined"
                    disabled={window.appUser.type !== UserType.CUSTOMER}
                    fullWidth
                    validate={validateRequired}
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="lastName"
                    label="Last Name"
                    variant="outlined"
                    disabled={window.appUser.type !== UserType.CUSTOMER}
                    fullWidth
                    validate={validateRequired}
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="email"
                    type="email"
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    variant="outlined"
                    fullWidth
                    disabled={true}
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    validate={validatePhone}
                    disabled={window.appUser.type !== UserType.CUSTOMER}
                    variant="outlined"
                    fullWidth
                    helperText={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="reloadYardAccounts.items[0].description"
                    label="Description"
                    variant="outlined"
                    fullWidth
                    helperText={false}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container direction="column" spacing={1}>
              {aclList?.length > 0 && (
                <Grid item xs={12}>
                  <CheckboxGroupInput
                    source="reloadYardAccounts.items[0].acl"
                    label={
                      <>
                        Access Permissions{" "}
                        <NewBadge label="Access Permissions" />
                      </>
                    }
                    row={false}
                    fullWidth
                    choices={aclList}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CustomerEditActions = (props) => (
  <TopToolbar>
    <HelpButton>
      <CustomerEditHelp {...props} />
    </HelpButton>
    <SendInviteButton label="INVITE" title="Resend invitation" {...props} />
    <BackButton {...props} />
  </TopToolbar>
);

const CustomerEditToolbar = ({ record, ...props }) => (
  <Toolbar style={{ justifyContent: "space-between" }} {...props}>
    <ValidateSaveButton {...props} record={record} />
    <DeepDeleteButton record={record} />
  </Toolbar>
);

export const CustomerEdit = (props) => {
  const { record } = useEditController(props);
  const backPath = getBackPath(props);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit User" />}
      mutationMode="pessimistic"
      actions={<CustomerEditActions backPath={backPath} />}
      aside={<CustomerAside {...props} backPath={backPath} />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={<CustomerEditToolbar record={record} />}
      >
        <CustomerEditForm record={record} />
      </SimpleForm>
    </Edit>
  );
};
